import React from 'react';
import { motion } from 'framer-motion';
import { PROCESS_STEPS } from '../../lib/constants';
import { 
    MessageSquare, Globe, Plane, Activity, HeartPulse, Home, CheckCircle, Zap, Stethoscope, Award
} from 'lucide-react';
import { Button } from '../ui/Button';

export const Process: React.FC = () => {
    const icons = [MessageSquare, Globe, Stethoscope, Plane, Home, CheckCircle, Activity, Zap, HeartPulse, Stethoscope, Award];

    const scrollToContact = () => {
        document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <section id="process" className="py-20 md:py-32 bg-white overflow-hidden">
            <div className="container mx-auto px-6">
                <div className="text-center mb-16 md:mb-24">
                    <span className="text-[#11BC97] font-bold tracking-widest uppercase text-xs md:text-sm mb-4 block flex items-center justify-center gap-2">
                         <span className="w-8 h-[1px] bg-[#11BC97]"></span>
                        Roadmap
                         <span className="w-8 h-[1px] bg-[#11BC97]"></span>
                    </span>
                    <h2 className="text-3xl md:text-5xl font-bold mb-6 text-black">Your Journey to New Heights</h2>
                    <p className="text-gray-500 max-w-2xl mx-auto text-base md:text-lg">Mapping out your route to growth.</p>
                </div>

                <div className="relative max-w-6xl mx-auto">
                    <div className="space-y-0 relative">
                        {/* Vertical line (desktop only) */}
                        <div className="hidden lg:block absolute left-1/2 top-16 bottom-0 w-0.5 bg-gradient-to-b from-[#11BC97] via-[#11BC97] to-transparent transform -translate-x-1/2" />

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-y-16 lg:gap-y-20 lg:gap-x-0 relative">
                            {PROCESS_STEPS.map((step, idx) => {
                                const Icon = icons[idx] || MessageSquare;
                                const isEven = idx % 2 === 0;
                                // Add top margin to odd-indexed cards for Z pattern
                                const zPatternMargin = !isEven ? 'lg:mt-24' : '';
                                return (
                                    <motion.div 
                                        key={step.id}
                                        initial={{ opacity: 0, x: isEven ? -40 : 40 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: idx * 0.12, duration: 0.6 }}
                                        className={`flex gap-6 lg:gap-0 items-start ${isEven ? 'lg:pr-16 lg:flex-row-reverse lg:text-right' : 'lg:pl-16 lg:text-left'} ${zPatternMargin}`}
                                    >
                                        {/* Card Content */}
                                        <div className="flex-1">
                                            <motion.div
                                                whileHover={{ y: -6 }}
                                                className="bg-white p-7 md:p-9 rounded-2xl border border-gray-200 shadow-md hover:shadow-2xl hover:border-[#11BC97]/50 transition-all duration-300 group cursor-pointer h-full"
                                            >
                                                <div className="space-y-4">
                                                    <div className="flex items-start gap-4 lg:gap-0 lg:flex-col lg:space-y-3">
                                                        <div className="flex-shrink-0 w-14 h-14 lg:w-12 lg:h-12 rounded-xl bg-[#11BC97]/15 flex items-center justify-center group-hover:bg-[#11BC97]/25 transition-colors">
                                                            <Icon className="w-7 h-7 lg:w-6 lg:h-6 text-[#11BC97]" />
                                                        </div>
                                                        <div className="flex-1">
                                                            <h3 className="text-xl lg:text-lg font-bold text-black leading-snug">{step.title}</h3>
                                                        </div>
                                                    </div>
                                                    <p className="text-sm lg:text-sm text-gray-600 leading-relaxed font-medium">{step.description}</p>
                                                </div>
                                            </motion.div>
                                        </div>

                                        {/* Center Circle with Number */}
                                        <div className="flex-shrink-0 hidden lg:flex items-center justify-center py-3">
                                            <motion.div 
                                                whileHover={{ scale: 1.2 }}
                                                className="relative flex items-center justify-center"
                                            >
                                                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#11BC97] to-[#0a9d7f] flex items-center justify-center shadow-2xl z-10 border-4 border-white hover:shadow-[#11BC97]/30 transition-all">
                                                    <span className="text-white font-bold text-lg">{step.id}</span>
                                                </div>
                                            </motion.div>
                                        </div>

                                        {/* Mobile number badge */}
                                        <div className="flex-shrink-0 lg:hidden">
                                            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#11BC97] to-[#0a9d7f] flex items-center justify-center text-white font-bold text-base shadow-lg border-2 border-white">
                                                {step.id}
                                            </div>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="mt-16 md:mt-24 text-center">
                    <Button onClick={scrollToContact}>Start Your Journey</Button>
                </div>
            </div>
        </section>
    );
};